
/**
 * Shared hook for location bookmark state: saved IDs, save counts, busy IDs,
 * hydration of the user's saved locations, and throttled count fetching.
 *
 * Extracted from ListTimelineList, ItineraryTimelineList, and PostViewer to
 * eliminate ~80 lines of copy-paste per consumer.
 */

import {
  useState,
  useEffect,
  useRef,
  useCallback,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { usePlatformBrowseMode } from '@/contexts';
import {
  fetchSavedLocationsForUser,
  fetchLocationSaveCount,
} from '@/api/locationSaves';
import { debug } from '@/utils/logger';

const MAX_PAGES = 10;
const PAGE_SIZE = 50;
const COUNT_FETCH_CONCURRENCY = 4;
const COUNT_FETCH_RETRY_DELAY = 300;

export interface UseLocationBookmarkStateParams {
  user: { _id?: string | number } | null;
  /** Set to true when the component is in viewer (non-edit) mode and the user
   *  is not the author — i.e. when we should hydrate the user's saved IDs. */
  shouldHydrate: boolean;
}

export interface UseLocationBookmarkStateReturn {
  userSavedLocationIds: Set<string>;
  setUserSavedLocationIds: Dispatch<SetStateAction<Set<string>>>;
  locationSaveCountById: Map<string, number>;
  setLocationSaveCountById: Dispatch<SetStateAction<Map<string, number>>>;
  saveBusyIds: Set<string>;
  setSaveBusyIds: Dispatch<SetStateAction<Set<string>>>;
  enqueueSaveCountFetch: (locationId: string) => void;
}

export function useLocationBookmarkState(
  params: UseLocationBookmarkStateParams,
): UseLocationBookmarkStateReturn {
  const { user, shouldHydrate } = params;
  const { browseMode, isLoading: isBrowseModeLoading } = usePlatformBrowseMode();
  const locationSaveCountBrowseMode = isBrowseModeLoading ? undefined : browseMode;

  const [userSavedLocationIds, setUserSavedLocationIds] = useState<Set<string>>(new Set());
  const [locationSaveCountById, setLocationSaveCountById] = useState<Map<string, number>>(new Map());
  const [saveBusyIds, setSaveBusyIds] = useState<Set<string>>(new Set());

  const countFetchQueuedRef = useRef<Set<string>>(new Set());
  const countFetchInFlightRef = useRef(0);

  // Hydrate the set of location ids the user has already saved.
  useEffect(() => {
    if (!user || !shouldHydrate) return;

    let cancelled = false;
    const ids = new Set<string>();

    async function hydrate() {
      for (let page = 0; page < MAX_PAGES; page++) {
        try {
          const items = await fetchSavedLocationsForUser({
            limit: PAGE_SIZE,
            skip: page * PAGE_SIZE,
          });
          if (cancelled) return;
          if (!items.length) break;
          for (const item of items) {
            const id = item.location_id || item.location?._id;
            if (id) ids.add(id);
          }
          if (items.length < PAGE_SIZE) break;
        } catch {
          break;
        }
      }
      if (!cancelled) {
        setUserSavedLocationIds(new Set(ids));
        debug('[useLocationBookmarkState] Hydrated saved location ids', { count: ids.size });
      }
    }

    hydrate();
    return () => { cancelled = true; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?._id, shouldHydrate]);

  const enqueueSaveCountFetch = useCallback((locationId: string) => {
    if (!locationId) return;
    if (countFetchQueuedRef.current.has(locationId)) return;
    countFetchQueuedRef.current.add(locationId);

    async function doFetch() {
      if (countFetchInFlightRef.current >= COUNT_FETCH_CONCURRENCY) {
        setTimeout(() => {
          countFetchQueuedRef.current.delete(locationId);
          enqueueSaveCountFetch(locationId);
        }, COUNT_FETCH_RETRY_DELAY);
        return;
      }
      countFetchInFlightRef.current++;
      try {
        const count = await fetchLocationSaveCount(locationId, {
          browseMode: locationSaveCountBrowseMode,
        });
        setLocationSaveCountById((prev) => {
          const next = new Map(prev);
          next.set(locationId, count);
          return next;
        });
      } catch {
        // Count is non-critical; display will just be blank.
      } finally {
        countFetchInFlightRef.current--;
      }
    }

    doFetch();
  }, [locationSaveCountBrowseMode]);

  return {
    userSavedLocationIds,
    setUserSavedLocationIds,
    locationSaveCountById,
    setLocationSaveCountById,
    saveBusyIds,
    setSaveBusyIds,
    enqueueSaveCountFetch,
  };
}
